import React, { useEffect, useState } from 'react';
import { addProduct, getShopifyProducts, updateProduct } from '../API/product';
import { useLinesContext } from '../../contexts/Lines';
import { useSnackbarContext } from '../../contexts/Snackbar';
import { Form, Formik } from 'formik';
import { productQuestions } from './questions';
import { useCompanyContext } from '../../contexts/Company';
import { ESellerStore } from '../../Types/business';
import cloneDeep from 'lodash.clonedeep';
import makeStyles from '@material-ui/core/styles/makeStyles';
import productValidations from './validation';
import FormikField from '../misc/FormikField';
import DialogActions from '../BuildingBlocks/Dialogs/DialogStyleOne/DialogActions';
import SubmitAndCancelButtons from '../BuildingBlocks/Dialogs/DialogStyleOne/SubmitAndCancelButtons';
import ProductCarousel from './ProductCarousel';
import Loader from '../Loader';

const useStyles = makeStyles((theme: any) => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 450,
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 300,
    minWidth: 400,
  },
  form: { marginBottom: theme.spacing(2) },
  description: { textAlign: 'center' },
  field: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: 'white',
  },
}));

export default function ConnectProductForm(props) {
  const classes = useStyles();
  const snackbar = useSnackbarContext();
  const {
    handleClose,
    addProductToState,
    selectedProduct = null,
    setProducts,
    products,
  } = props;
  const [dialogSelectedProduct, setDialogSelectedProduct] =
    useState(selectedProduct);
  const [productList, setProductList] = useState([]);
  const { createLineFromScratch } = useLinesContext();
  const { business } = useCompanyContext();
  const isAmazonSeller = business.sellerStore === ESellerStore.amazon;
  const isShopifySeller = business.sellerStore === ESellerStore.shopify;
  const shouldDisplayCarusel = isShopifySeller && productList?.length > 0;
  const [loading, setLoading] = useState(isShopifySeller && !selectedProduct);

  async function getProductListWrapper() {
    try {
      if (isShopifySeller && !selectedProduct) {
        const shopifyProducts = await getShopifyProducts();
        setProductList(shopifyProducts);
      }
    } catch (error) {
      console.log(`failed to get shopify products`, error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getProductListWrapper();
    // eslint-disable-next-line
  }, []);

  const submitNewProduct = async (
    values,
    productUrl,
    numShipmentsLastYear,
    msrp,
  ) => {
    const product = await addProduct({
      name: values.product,
      asin: values.asin,
      url: productUrl,
      numShipmentsLastYear,
      msrp: msrp,
      image: values.image,
    });
    const newProduct = {
      id: product.id,
      name: values.product,
      asin: values.asin,
      url: productUrl,
      numShipmentsLastYear,
      msrp: msrp,
      image: values.image,
      createdAt: new Date(),
    };
    if (addProductToState) {
      addProductToState(newProduct);
    }
    createLineFromScratch(product.id);
  };

  const updateExistingProduct = async (
    values,
    productUrl,
    numShipmentsLastYear,
    msrp,
  ) => {
    await updateProduct(selectedProduct.id, {
      name: values.product,
      asin: values.asin,
      url: productUrl,
      numShipmentsLastYear,
      msrp: msrp,
    });

    let updatedProducts = cloneDeep(products);
    updatedProducts = updatedProducts.map((product) => {
      if (product.id === selectedProduct.id) {
        product.name = values.product;
        product.asin = values.asin;
        product.url = productUrl;
        product.numShipmentsLastYear = numShipmentsLastYear;
        product.msrp = values.msrp;
      }
      return product;
    });
    setProducts(updatedProducts);
  };

  function onSelect(id: string) {
    setDialogSelectedProduct(productList.find((product) => product.id === id));
  }

  return (
    <Formik
      initialValues={{
        product: dialogSelectedProduct?.name || '',
        asin: dialogSelectedProduct?.asin || '',
        url: dialogSelectedProduct?.url || '',
        shipments: dialogSelectedProduct
          ? String(dialogSelectedProduct.numShipmentsLastYear)
          : '',
        msrp: dialogSelectedProduct ? String(dialogSelectedProduct.msrp) : '',
        image: dialogSelectedProduct?.image,
      }}
      validationSchema={productValidations({ isAmazonSeller })}
      onSubmit={async (values) => {
        const numShipmentsLastYear = Number.parseInt(
          values.shipments.replace(/,/g, ''),
        );
        const msrp = Number.parseFloat(values.msrp.replace(/,/g, ''));
        try {
          const productUrl = isAmazonSeller
            ? `https://www.amazon.com/dp/${values.asin}`
            : `${values.url}`;

          if (selectedProduct) {
            await updateExistingProduct(
              values,
              productUrl,
              numShipmentsLastYear,
              msrp,
            );
          } else {
            await submitNewProduct(
              values,
              productUrl,
              numShipmentsLastYear,
              msrp,
            );
          }

          handleClose();
          return snackbar.show(
            `Successfully ${
              selectedProduct ? 'edited' : 'added new'
            }  product ${values.product}.`,
            'success',
          );
        } catch (error) {
          return snackbar.show(
            'Oops, we encountered an issue while trying to save your product information. Please try again.',
            'error',
          );
        }
      }}
      enableReinitialize={true}
      component={({ submitForm, isSubmitting }) => {
        return (
          <div className={classes.dialogContent}>
            {loading ? (
              <Loader />
            ) : (
              <>
                {shouldDisplayCarusel && (
                  <ProductCarousel
                    products={productList}
                    handleOnSelect={onSelect}
                    selectedProduct={dialogSelectedProduct}
                  />
                )}
                <Form className={classes.form}>
                  {productQuestions
                    .filter(
                      (q, i) =>
                        (isAmazonSeller && i !== 2) ||
                        (!isAmazonSeller && i !== 1),
                    )
                    .map((question) => (
                      <div className={classes.field} key={question.value}>
                        <FormikField
                          field={question}
                          width={300}
                          disabled={
                            shouldDisplayCarusel &&
                            question.value !== 'shipments'
                          }
                        />
                      </div>
                    ))}
                </Form>
                <DialogActions>
                  <SubmitAndCancelButtons
                    text={selectedProduct ? 'Save' : 'Add'}
                    color="primary"
                    variant="contained"
                    submitForm={submitForm}
                    isSubmitting={isSubmitting}
                    handleClose={handleClose}
                  />
                </DialogActions>
              </>
            )}
          </div>
        );
      }}
    />
  );
}
